import React, { VFC, useEffect, useState } from 'react';
import { StepEditor, StepEditorProps } from 'src/editor/step-editor';
import { StepEditorContentConfigureStageTypeEnum } from 'src/editor/step-editor/content/configure/stage-type.enum';
import { StepEditorInitialStage } from 'src/editor/step-editor/initial-stage';
import { useEditorContext } from 'src/store/editor';
import { useStepEditorContext } from 'src/store/step-editor';
import { apiGetJourney, apiPostJourneyInteractionOpen } from 'src/utils/journeyApi';
import { EventBus } from 'src/common/EventBus';
import { usePreviousValue } from 'src/utils/react/previous-value.hook';
import { STEP_EDITOR_CONTENT_OPTION_INITIAL } from 'src/editor/constants';
import { useHistory } from 'react-router-dom';
import { Step } from 'src/common/interfaces/step.interface';
import { Node } from 'src/common/interfaces/node.interface';
import JoyrideOnboardingGuide from 'src/onboarding/joyride-onboarding';


interface PageJourneysEditProps extends Pick<StepEditorProps, 'nodeId'> {
  shareTabValue: string;
  uuid: string;
  nodeId?: string;
  stepId?: string;
  documentId?: string;
  pageConfigureStage?: StepEditorContentConfigureStageTypeEnum;
}

export const PageJourneysEdit: VFC<PageJourneysEditProps> = ({
  uuid,
  nodeId,
  stepId,
  documentId,
  pageConfigureStage = StepEditorContentConfigureStageTypeEnum.CONFIGURE,
}) => {
  const history = useHistory();
  const {
    node_id,
    resetOldContentBehavior,
    setContentOption,
    setAnnotation,
    currentConfigurationStage,
    annotation,
    setCurrentConfigurationStage,
    resetContent,
    setStateFromNode,
    resetStepEditor,
  } = useStepEditorContext();
  const { journey, setJourney, resetEditor, currentActiveNodeId } = useEditorContext();
  const prevActiveConfigureStageRef = usePreviousValue(pageConfigureStage);
  const [showInitialStage, setShowInitialStage] = useState(
    pageConfigureStage === StepEditorContentConfigureStageTypeEnum.NEW
  );


  useEffect(() => {
    const onLocationChanged = (location: URL, action: any) => {
      if (location.pathname.endsWith('content')) {
        if (journey && currentActiveNodeId) {
          const nodeToMoveState = journey.steps
            .map((step: Step) => step.nodes)
            .flat()
            .find((node: Node) => node.id === currentActiveNodeId);
          if (nodeToMoveState) {
            setStateFromNode(nodeToMoveState);
          }
        }
        setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.BOARD);
        setShowInitialStage(false);
      } else if (location.pathname.endsWith(StepEditorContentConfigureStageTypeEnum.NEW) && !showInitialStage) {
        EventBus.emit('editor:reset-stage', {
          resetEditor: true,
          showInitialStage: true,
          currentConfigurationStage: StepEditorContentConfigureStageTypeEnum.NEW,
        });
      }
    };

    const historyChangeListener = history.listen(onLocationChanged);

    return () => {
      historyChangeListener();
    };
  }, [history, showInitialStage, currentActiveNodeId, journey, setStateFromNode, setCurrentConfigurationStage]);

  useEffect(() => {
    if (pageConfigureStage) {
      if (pageConfigureStage !== prevActiveConfigureStageRef) {
        if (prevActiveConfigureStageRef === StepEditorContentConfigureStageTypeEnum.NEW && showInitialStage) {
          setShowInitialStage(false);
        }
      }
      if (
        prevActiveConfigureStageRef === StepEditorContentConfigureStageTypeEnum.BOARD &&
        pageConfigureStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE
      ) {
        setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
      }
    }
  }, [
    pageConfigureStage,
    prevActiveConfigureStageRef,
    setCurrentConfigurationStage,
    currentConfigurationStage,
    showInitialStage,
  ]);

  useEffect(() => {
    if (node_id) {
      resetOldContentBehavior();
    }
  }, [node_id, resetOldContentBehavior]);

  useEffect(() => {
    const onEditorResetStage = (
      params: {
        showInitialStage: boolean;
        overwriteValues?: object;
        resetEditor?: boolean;
        currentConfigurationStage?: StepEditorContentConfigureStageTypeEnum;
      } = {
        showInitialStage: true,
      }
    ) => {
      if (params.resetEditor) {
        resetStepEditor(params.overwriteValues);
      }
      setShowInitialStage(params.showInitialStage);
      setCurrentConfigurationStage(
        params.currentConfigurationStage || StepEditorContentConfigureStageTypeEnum.CONFIGURE
      );
    };

    EventBus.on('editor:reset-stage', onEditorResetStage);

    return () => {
      EventBus.off('editor:reset-stage', onEditorResetStage);
    };
  }, [setCurrentConfigurationStage, resetStepEditor, setContentOption]);

  useEffect(() => {
    const onEditorResetContent = () => {
      resetContent();
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.NEW);
      setContentOption(STEP_EDITOR_CONTENT_OPTION_INITIAL);
      setShowInitialStage(true);
    };

    EventBus.on('editor:reset-content', onEditorResetContent);

    return () => {
      EventBus.off('editor:reset-content', onEditorResetContent);
    };
  }, [setContentOption, resetContent, setCurrentConfigurationStage, setShowInitialStage, setAnnotation, annotation]);

  useEffect(() => {
    const fetchJourney = async (uuid: string) => {
      const jny = await apiGetJourney(uuid);
      setJourney(jny);
      apiPostJourneyInteractionOpen(uuid);
      if (pageConfigureStage) {
        setCurrentConfigurationStage(pageConfigureStage);
      }
    };

    if (uuid) {
      fetchJourney(uuid);
    }
    // empty out journey to avoid flicker if the user loads up another journey
    return () => {
      resetEditor();
      resetStepEditor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  if (!journey) return null;

  if (showInitialStage) {
    return (
      <>
        <StepEditorInitialStage stepId={stepId} />
        <JoyrideOnboardingGuide initial={true} journey={journey} />
      </>
    );
  }

  return (
    <>
      <StepEditor 
        activeStage={pageConfigureStage} 
        nodeId={nodeId} 
        stepId={stepId} 
        documentId={documentId} 
      />
      <JoyrideOnboardingGuide initial={true} journey={journey} />
    </>
  );
};
